import { createSlice } from '@reduxjs/toolkit'

const loadFromStorage = () => {
    try {
        const token = localStorage.getItem('token')
        const refreshToken = localStorage.getItem('refreshToken')
        if (!token && !refreshToken) return null

        // El acceso persiste mientras el refresh token siga vivo (idle timeout deslizante de 7 días),
        // aunque el access token de 15 min ya haya caducado: se renovará solo en la siguiente petición
        const refreshExpirationDate = new Date(localStorage.getItem('refreshExpirationDate'))
        if (!refreshToken || refreshExpirationDate <= new Date()) {
            localStorage.clear()
            return null
        }

        return {
            token,
            refreshToken,
            userId: localStorage.getItem('userId'),
            userName: localStorage.getItem('userName'),
            userFullName: localStorage.getItem('userFullName'),
            userFlavor: localStorage.getItem('userFlavor'),
            userToken: localStorage.getItem('userToken'),
            role: localStorage.getItem('role'),
            permissions: localStorage.getItem('permissions')?.split(',') || [],
            settings: JSON.parse(localStorage.getItem('settings') || '{}')
        }
    } catch {
        return null
    }
}

const persisted = loadFromStorage()

const initialState = {
    token: persisted?.token ?? null,
    refreshToken: persisted?.refreshToken ?? null,
    userId: persisted?.userId ?? null,
    userName: persisted?.userName ?? null,
    userFullName: persisted?.userFullName ?? null,
    userFlavor: persisted?.userFlavor ?? null,
    userToken: persisted?.userToken ?? null,
    role: persisted?.role ?? null,
    permissions: persisted?.permissions ?? [],
    settings: persisted?.settings ?? {},
    error: null,
    loading: false
}

// Si localStorage lanza (cuota excedida, modo privado, storage bloqueado por política...) NO debe
// tirar dentro del recipe de Immer: un throw ahí hace que se descarte TODO el estado ya asignado
// en el reducer (incluido loading=false), dejando el spinner de login colgado para siempre sin error visible
const safeSetItem = (key, value) => {
    try {
        localStorage.setItem(key, value)
    } catch (err) {
        console.error(`[auth] No se pudo persistir '${key}' en localStorage`, err)
    }
}

const persistTokens = ({ token, expiresIn, refreshToken, refreshExpiresIn }) => {
    const expirationDate = new Date(Date.now() + expiresIn * 1000)
    const refreshExpirationDate = new Date(Date.now() + refreshExpiresIn * 1000)
    safeSetItem('token', token)
    safeSetItem('expirationDate', expirationDate.toISOString())
    safeSetItem('refreshToken', refreshToken)
    safeSetItem('refreshExpirationDate', refreshExpirationDate.toISOString())
}

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginStart(state) {
            state.loading = true
            state.error = null
        },
        loginSuccess(state, action) {
            const {
                token,
                userId,
                userName,
                userFullName,
                userFlavor,
                userToken,
                role,
                permissions,
                settings,
                expiresIn,
                refreshToken,
                refreshExpiresIn
            } = action.payload
            state.token = token
            state.refreshToken = refreshToken
            state.userId = userId
            state.userName = userName
            state.userFullName = userFullName
            state.userFlavor = userFlavor
            state.userToken = userToken
            state.role = role
            state.permissions = Array.isArray(permissions)
                ? permissions
                : permissions?.split(',') || []
            state.settings = settings || {}
            state.loading = false
            state.error = null

            // Persist to localStorage
            persistTokens({ token, expiresIn, refreshToken, refreshExpiresIn })
            safeSetItem('userId', userId)
            safeSetItem('userName', userName || '')
            safeSetItem('userFullName', userFullName)
            safeSetItem('userFlavor', userFlavor || '')
            safeSetItem('userToken', userToken || '')
            safeSetItem('role', role)
            safeSetItem(
                'permissions',
                Array.isArray(permissions) ? permissions.join(',') : permissions || ''
            )
            safeSetItem('settings', JSON.stringify(settings || {}))
        },
        // Renovación silenciosa: solo actualiza los tokens, sin tocar el resto de la sesión del usuario
        tokenRefreshed(state, action) {
            const { token, expiresIn, refreshToken, refreshExpiresIn } = action.payload
            state.token = token
            state.refreshToken = refreshToken

            persistTokens({ token, expiresIn, refreshToken, refreshExpiresIn })
        },
        // Sincroniza en memoria los tokens que OTRA pestaña ya rotó y persistió en localStorage.
        // No vuelve a escribir en localStorage (evita bucles del evento 'storage' y expiraciones incorrectas)
        tokensSynced(state, action) {
            const { token, refreshToken } = action.payload
            state.token = token
            state.refreshToken = refreshToken
        },
        loginFail(state, action) {
            state.loading = false
            state.error = action.payload
        },
        logout(state) {
            state.token = null
            state.refreshToken = null
            state.userId = null
            state.userName = null
            state.userFullName = null
            state.userFlavor = null
            state.userToken = null
            state.role = null
            state.permissions = []
            state.settings = {}
            state.error = null
            state.loading = false

            localStorage.removeItem('token')
            localStorage.removeItem('expirationDate')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('refreshExpirationDate')
            localStorage.removeItem('userId')
            localStorage.removeItem('userName')
            localStorage.removeItem('userFullName')
            localStorage.removeItem('userFlavor')
            localStorage.removeItem('userToken')
            localStorage.removeItem('role')
            localStorage.removeItem('permissions')
            localStorage.removeItem('settings')
        },
        updateSettings(state, action) {
            state.settings = action.payload
            safeSetItem('settings', JSON.stringify(action.payload))
        },
        clearError(state) {
            state.error = null
        }
    }
})

export const {
    loginStart,
    loginSuccess,
    tokenRefreshed,
    tokensSynced,
    loginFail,
    logout,
    updateSettings,
    clearError
} = authSlice.actions

// Selectors
export const selectIsAuthenticated = (state) => !!state.auth.token
export const selectUser = (state) => ({
    id: state.auth.userId,
    username: state.auth.userName,
    fullName: state.auth.userFullName,
    flavor: state.auth.userFlavor,
    role: state.auth.role
})
export const selectPermissions = (state) => state.auth.permissions
export const selectSettings = (state) => state.auth.settings
export const selectAuthError = (state) => state.auth.error
export const selectAuthLoading = (state) => state.auth.loading

export default authSlice.reducer
